import type { RelativeTimeFormatSingularUnit } from '@formatjs/ecma402-abstract';
import type { FormatRelativeTimeOptions } from '@formatjs/intl';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
const MINUTE = 60;
const HOUR = 60 * 60;
const DAY = 60 * 60 * 24;
function selectUnit(seconds: number): RelativeTimeFormatSingularUnit {
  const absValue = Math.abs(seconds);
  if (absValue < MINUTE) {
    return 'second';
  }
  if (absValue < HOUR) {
    return 'minute';
  }
  if (absValue < DAY) {
    return 'hour';
  }
  return 'day';
}
function getDurationInSeconds(unit?: RelativeTimeFormatSingularUnit): number {
  switch (unit) {
    case 'second':
      return 1;
    case 'minute':
      return MINUTE;
    case 'hour':
      return HOUR;
    default:
      return DAY;
  }
}
function valueToSeconds(value?: number, unit?: RelativeTimeFormatSingularUnit): number {
  if (!value) {
    return 0;
  }
  switch (unit) {
    case 'second':
      return value;
    case 'minute':
      return value * MINUTE;
    default:
      return value * HOUR;
  }
}
function excludeUnionWords(a: string, b: string) {
  const ap = a.split(' ');
  const bp = b.split(' ');
  const exclude = [...ap.filter(c => !bp.includes(c)), ...bp.filter(c => !ap.includes(c))];
  return ap.filter(p => !exclude.includes(p)).join(' ');
}
export interface Props extends FormatRelativeTimeOptions {
  value?: number;
  unit?: RelativeTimeFormatSingularUnit;
  updateIntervalInSeconds?: number;
  onExpiryWarning?: () => void;
  onExpire?: () => void;
  children?(value: string): React.ReactElement<any> | null;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const INCREMENTABLE_UNITS: RelativeTimeFormatSingularUnit[] = ['second', 'minute', 'hour', 'day'];
function canIncrement(unit: RelativeTimeFormatSingularUnit = 'second'): boolean {
  return INCREMENTABLE_UNITS.indexOf(unit) > -1;
}
const SimpleFormattedRelativeTime: React.FC<Omit<Props, 'updateIntervalInSeconds'>> = props => {
  const {
    formatRelativeTime,
    textComponent: Text
  } = useIntl();
  const {
    children,
    value,
    unit,
    ...otherProps
  } = props;
  const formattedRelativeTimeValue = formatRelativeTime(value || 0, unit, otherProps);
  const negativeRelativeTime = formatRelativeTime(value ? -value : -1, unit, otherProps);
  const formattedRelativeTime = excludeUnionWords(formattedRelativeTimeValue, negativeRelativeTime);
  if (typeof children === 'function') {
    return children(formattedRelativeTime);
  }
  if (Text) {
    return <Text>{formattedRelativeTime}</Text>;
  }
  return <>{formattedRelativeTime}</>;
};
export const FormattedDuration: React.FC<Props> = ({
  value = 0,
  unit = 'second',
  updateIntervalInSeconds,
  onExpiryWarning,
  onExpire,
  ...otherProps
}) => {
  const [prevUnit, setPrevUnit] = useState<RelativeTimeFormatSingularUnit | undefined>();
  const [canExtend, setCanExtend] = useState(true);
  const [prevValue, setPrevValue] = useState<number>(0);
  const [currentValueInSeconds, setCurrentValueInSeconds] = useState<number>(0);
  let updateTimer: NodeJS.Timeout;
  if (unit !== prevUnit || value !== prevValue) {
    setPrevValue(value || 0);
    setPrevUnit(unit);
    setCurrentValueInSeconds(canIncrement(unit) ? valueToSeconds(value, unit) : 0);
  }
  useEffect(() => {
    if (currentValueInSeconds <= 0 && onExpire) onExpire();
    if (!canExtend && currentValueInSeconds > 120) setCanExtend(true);
    if (canExtend && currentValueInSeconds <= 120) {
      setCanExtend(false);
      onExpiryWarning && onExpiryWarning();
    }
    function clearUpdateTimer() {
      clearTimeout(updateTimer);
    }
    clearUpdateTimer();
    // If there's no interval and we cannot increment this unit, do nothing
    if (!updateIntervalInSeconds || !canIncrement(unit)) {
      return clearUpdateTimer;
    }
    // Figure out the next interesting time
    const nextValueInSeconds = currentValueInSeconds - updateIntervalInSeconds;
    const nextUnit = selectUnit(nextValueInSeconds);
    // We've reached the max auto incrementable unit, don't schedule another update
    if (nextUnit === 'day') {
      return clearUpdateTimer;
    }
    const unitDuration = getDurationInSeconds(nextUnit);
    const remainder = nextValueInSeconds % unitDuration;
    const prevInterestingValueInSeconds = nextValueInSeconds - remainder;
    const nextInterestingValueInSeconds = prevInterestingValueInSeconds >= currentValueInSeconds ? prevInterestingValueInSeconds - unitDuration : prevInterestingValueInSeconds;
    const delayInSeconds = Math.abs(nextInterestingValueInSeconds - currentValueInSeconds);
    if (currentValueInSeconds !== nextInterestingValueInSeconds) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      updateTimer = setTimeout(() => setCurrentValueInSeconds(nextInterestingValueInSeconds), delayInSeconds * 1e3);
      if (onExpire && currentValueInSeconds < 0) {
        onExpire();
      }
    }
    return clearUpdateTimer;
  }, [currentValueInSeconds, updateIntervalInSeconds, unit]);
  let currentValue = value || 0;
  let currentUnit = unit;
  if (canIncrement(unit) && typeof currentValueInSeconds === 'number' && updateIntervalInSeconds) {
    currentUnit = selectUnit(currentValueInSeconds);
    const unitDuration = getDurationInSeconds(currentUnit);
    currentValue = Math.round(currentValueInSeconds / unitDuration);
  }
  return <SimpleFormattedRelativeTime value={currentValue} unit={currentUnit} {...otherProps} />;
};
FormattedDuration.displayName = 'FormattedDuration';